import { arrow, autoPlacement, computePosition, flip, offset, shift } from '@floating-ui/dom'
import { isDesktop } from 'assets/core/js/common'
import type { Coords, Placement } from '@floating-ui/dom'

let setTimeoutId: ReturnType<typeof setTimeout> | null = null

interface TooltipConfig {
  delay?: number
  callbackShow: (tooltipEl: HTMLElement) => void
}

export const setTooltipContent = (tooltipEl: HTMLElement, content: string): void => {
  const contentEl = tooltipEl.querySelector<HTMLElement>('.content')

  if (!contentEl) {
    return
  }

  contentEl.innerHTML = content
}

export const showTooltip = (referenceEl: HTMLElement, tooltipEl: HTMLElement, config?: TooltipConfig): void => {
  const delay = config?.delay && isDesktop() ? config.delay : 0

  if (!tooltipEl.hasAttribute('hidden')) {
    return
  }

  if (setTimeoutId) {
    clearTimeout(setTimeoutId)
  }

  setTimeoutId = setTimeout(() => {
    tooltipEl.removeAttribute('hidden')
    updateTooltipPosition(referenceEl, tooltipEl)
      .then(() => true)
      .catch(() => true)
    setTimeoutId = null

    config?.callbackShow?.(tooltipEl)
  }, delay)
}

export const hideTooltip = (e: TouchEvent | MouseEvent, _referenceEl: HTMLElement, tooltipEl: HTMLElement): void => {
  if (tooltipEl.hasAttribute('hidden')) {
    return
  }

  if (setTimeoutId) {
    clearTimeout(setTimeoutId)
  }

  if (window.TouchEvent && e instanceof TouchEvent) {
    tooltipEl.setAttribute('hidden', 'hidden')
    return
  }

  if ((e as MouseEvent).relatedTarget !== tooltipEl) {
    tooltipEl.setAttribute('hidden', 'hidden')
  }
}

export const updateTooltipPosition = async (referenceEl: HTMLElement, tooltipEl: HTMLElement): Promise<void> => {
  const arrowEl = tooltipEl.querySelector<HTMLElement>(`.arrow`)

  if (!arrowEl) {
    return
  }

  const placementMiddleware = tooltipEl.hasAttribute('data-lock-position')
    ? null
    : tooltipEl.hasAttribute('data-position')
      ? flip()
      : autoPlacement({ crossAxis: true })

  const { x, y, placement, middlewareData } = await computePosition(referenceEl, tooltipEl, {
    placement: (tooltipEl.getAttribute('data-position') ?? 'top') as Placement,
    middleware: [placementMiddleware, shift({ padding: 8 }), arrow({ element: arrowEl, padding: 8 }), offset(6)],
  })

  tooltipEl.setAttribute('data-placement', tooltipEl.getAttribute('data-position') ?? placement)

  Object.assign(tooltipEl.style, {
    left: `${x}px`,
    top: `${y}px`,
  })

  const { x: arrowX, y: arrowY } = middlewareData.arrow as Coords

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0] as string] as string

  Object.assign(arrowEl.style, {
    left: arrowX != null ? `${arrowX}px` : '',
    top: arrowY != null ? `${arrowY}px` : '',
    right: '',
    bottom: '',
    [staticSide]: '-4px',
  })
}

export default function (triggerEl: HTMLElement, tooltipEl: HTMLElement, config?: TooltipConfig): void {
  if (!triggerEl || !tooltipEl) {
    return
  }

  if (tooltipEl.hasAttribute('data-init')) {
    return
  }

  tooltipEl.setAttribute('data-init', 'true')

  triggerEl.addEventListener('mouseenter', () => isDesktop() && showTooltip(triggerEl, tooltipEl, config))
  triggerEl.addEventListener('touchstart', () => showTooltip(triggerEl, tooltipEl, config))
  triggerEl.addEventListener('mouseleave', (e) => isDesktop() && hideTooltip(e, triggerEl, tooltipEl))
  tooltipEl.addEventListener('mouseleave', (e) => (e.target as HTMLElement) !== triggerEl && isDesktop() && hideTooltip(e, triggerEl, tooltipEl))
  document.body.addEventListener('touchstart', (e) => hideTooltip(e, triggerEl, tooltipEl))

  window.addEventListener('resize', () => {
    if (!tooltipEl.hasAttribute('hidden')) {
      updateTooltipPosition(triggerEl, tooltipEl)
        .then(() => true)
        .catch(() => true)
    }
  })
}
